// src/index.tsx
import "@/index.css"
import React, { Suspense } from "react"
import ReactDOM from "react-dom/client"
import { Provider } from "react-redux"
import reportWebVitals from "@/reportWebVitals"
import store from "@core/store"
import dayjs from "dayjs"
import "dayjs/locale/ko"
import "@core/utils/dayjs-locale-kr"
import theme from "@core/theme"
import { ThemeProvider } from "@mui/material"
import { createHashRouter, RouterProvider } from "react-router-dom"
import { SocketProvider } from "@core/contexts/SocketContext"
import { DemoModeProvider } from "@core/contexts/DemoModeContext"
import { Capacitor } from "@capacitor/core"
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar"
import AppBootFallback from "@shared/components/common/AppBootFallback"
import { applyWindowBootstrapState } from "@core/utils/serverConnectionBootstrap"
import { hasValidAuthSession } from "@core/utils/tokenValidator"
import {
  isPhoneWebBrowserEntry,
  resolveBareWebChartEntryRedirect,
  shouldNormalizeWebChartEntry,
  WEB_CHART_ROOT_ENTRY_REDIRECT_KEY,
} from "@core/utils/webChartEntryRoute"

const resizeObserverLoopErr = "ResizeObserver loop completed with undelivered notifications."

const isAppleWebPlatform = () => {
  if (window.electronAPI || Capacitor.isNativePlatform()) {
    return false
  }

  const userAgent = navigator.userAgent || ""
  const platform = navigator.platform || ""
  const maxTouchPoints = navigator.maxTouchPoints || 0
  const isElectron = /Electron\//.test(userAgent)
  const isMac = /Mac/.test(platform)
  const isIOS = /iPhone|iPad|iPod/.test(userAgent)
  const isIPadOS = platform === "MacIntel" && maxTouchPoints > 1

  return !isElectron && (isMac || isIOS || isIPadOS)
}

const shouldEnableFontSmoothing = () => {
  const userAgent = navigator.userAgent || ""
  const platform = navigator.platform || ""
  const maxTouchPoints = navigator.maxTouchPoints || 0
  const isElectron = /Electron\//.test(userAgent)
  const isNativePlatform = Capacitor.isNativePlatform()
  const capacitorPlatform = Capacitor.getPlatform()
  const isMac = /Mac/.test(platform)
  const isIOS = /iPhone|iPad|iPod/.test(userAgent)
  const isIPadOS = platform === "MacIntel" && maxTouchPoints > 1
  const isAndroidWeb = /Android/.test(userAgent)
  const isAppleWeb = !isNativePlatform && (isMac || isIOS || isIPadOS)
  const isMobileNative = isNativePlatform && (capacitorPlatform === "ios" || capacitorPlatform === "android")

  return !isElectron && (isAppleWeb || isAndroidWeb || isMobileNative)
}

if (isAppleWebPlatform()) {
  document.documentElement.classList.add("apple-web-font")
}

if (shouldEnableFontSmoothing()) {
  document.documentElement.classList.add("apple-android-font-smoothing")
}

const shouldNormalizeRootWebChartEntry = shouldNormalizeWebChartEntry({
  hasElectronApi: Boolean(window.electronAPI),
  isCapacitorApp: __IS_CAPACITOR__,
})

if (shouldNormalizeRootWebChartEntry) {
  const bareWebChartEntryRedirect = resolveBareWebChartEntryRedirect({
    pathname: window.location.pathname,
    hash: window.location.hash,
    hasValidSession: hasValidAuthSession(),
    preferMobileEntry: isPhoneWebBrowserEntry(),
  })

  if (bareWebChartEntryRedirect) {
    if (bareWebChartEntryRedirect.shouldRedirectToDashboard) {
      sessionStorage.setItem(WEB_CHART_ROOT_ENTRY_REDIRECT_KEY, "true")
    } else {
      sessionStorage.removeItem(WEB_CHART_ROOT_ENTRY_REDIRECT_KEY)
    }

    window.history.replaceState(
      window.history.state,
      "",
      `${bareWebChartEntryRedirect.nextPathname}${window.location.search}${bareWebChartEntryRedirect.nextHash}`
    )
  }
}

window.addEventListener("error", event => {
  if (event.message === resizeObserverLoopErr || event.message.includes("ResizeObserver loop limit exceeded")) {
    event.stopImmediatePropagation()
  }
})

window.onerror = (message, source, lineno, colno, error) => {
  console.error(" GlobalError:", { message, source, lineno, colno, error })
}

window.addEventListener("unhandledrejection", event => {
  console.error(" UnhandledRejection:", event.reason)
})

dayjs.locale("ko")

const windowBootstrapApplied = applyWindowBootstrapState()

// 개발 모드: VITE_DEV_SERVER_IP 환경변수가 설정되어 있으면 localStorage에 자동 주입
// (크롬 브라우저에서 Android UI 개발 시 서버 설정 화면을 건너뜀)
if (!windowBootstrapApplied && import.meta.env.VITE_DEV_SERVER_IP && !window.electronAPI && !__IS_CAPACITOR__) {
  const devIp = import.meta.env.VITE_DEV_SERVER_IP as string
  const currentIp = localStorage.getItem("SigmaServerIp")
  if (currentIp !== devIp) {
    localStorage.setItem("SigmaServerIp", devIp)
    console.log(`[DEV] SigmaServerIp 자동 설정: ${devIp}`)
  }
}

// Android/iOS: 상태바 검은색 배경, 하단 네비게이션바는 네이티브에서 숨김 처리
if (Capacitor.isNativePlatform()) {
  StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {})
  StatusBar.setStyle({ style: StatusBarStyle.Dark }).catch(() => {})
  StatusBar.setBackgroundColor({ color: "#000000" }).catch(() => {})
}

// 컴파일 타임 플랫폼 분기 — Vite define으로 __IS_CAPACITOR__가 리터럴 true/false로 치환되어
// 빌드 시 미사용 브랜치의 dynamic import가 완전히 제거됨 (데드 코드 0바이트 보장)
const PlatformApp = __IS_CAPACITOR__ ? React.lazy(() => import("@app/AndroidApp")) : React.lazy(() => import("@app/App"))

const container = document.getElementById("root")
if (!container) throw new Error("Root element not found")
const root = ReactDOM.createRoot(container)

const router = createHashRouter([
  {
    path: "/*",
    element: (
      <Suspense fallback={<AppBootFallback />}>
        <PlatformApp />
      </Suspense>
    ),
  },
])

root.render(
  <Provider store={store}>
    <DemoModeProvider>
      <SocketProvider>
        <ThemeProvider theme={theme}>
          <RouterProvider router={router} />
        </ThemeProvider>
      </SocketProvider>
    </DemoModeProvider>
  </Provider>
)

reportWebVitals()

